<template>
    <section v-if="lifecycle.steps.length" class="lc-card">
        <div class="lc-header">
            <div class="lc-title">
                Order progress<span class="lc-status" :class="`lc-status--${lifecycle.statusTone}`"> · {{
                    lifecycle.statusText }}</span>
            </div>

            <!-- <button type="button" class="lc-refresh" aria-label="Refresh tracking data" v-if="showUpdate"
                @click="$emit('refresh')">
                <span>{{ lifecycle.updatedLabel }}</span>
                <span aria-hidden="true" class="lc-refresh-icon">
                    <RefreshCw :size="13" />
                </span>
            </button> -->
        </div>

        <div class="lc-progress" :style="{ gridTemplateColumns: `repeat(${lifecycle.steps.length}, minmax(0, 1fr))` }">
            <div v-for="(step, index) in lifecycle.steps" :key="step.key" class="lc-step"
                :class="`lc-step--${step.state}`">
                <span class="lc-step-circle" :class="`lc-step-circle--${step.state}`">
                    <component :is="stepIcon(step.icon)" class="lc-step-icon" :size="14" />
                </span>

                <span v-if="index < lifecycle.steps.length - 1" class="lc-step-line"
                    :class="step.state === 'done' ? 'lc-step-line--done' : step.state === 'partial' ? 'lc-step-line--partial' : 'lc-step-line--future'" />

                <div class="lc-step-caption">{{ step.caption }}</div>
                <div class="lc-step-title">{{ step.title }}</div>
                <div v-if="step.detail" class="lc-step-desc">{{ step.detail }}</div>
            </div>
        </div>
    </section>
</template>

<script setup>
import { computed } from "vue";
import { Check, CreditCard, Package, Truck, House, Mail, RefreshCw, AlertCircle } from "lucide-vue-next";

defineEmits(["refresh"]);

const props = defineProps({
    order: {
        type: Object,
        default: () => ({}),
    },
    shipments: {
        type: Array,
        default: () => [],
    },
    showUpdate: {
        type: Boolean,
        default: false,
    },
});

const formatDate = (value) => {
    if (!value) return "";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);

    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    }).format(parsed);
};

const stepIcon = (icon) => {
    if (icon === "check") return Check;
    if (icon === "payment") return CreditCard;
    if (icon === "pack") return Package;
    if (icon === "ship") return Truck;
    if (icon === "delivered") return House;
    if (icon === "overdue") return AlertCircle;
    return Mail;
};

const normalizePaymentStatus = (value) =>
    String(value || "")
        .toLowerCase()
        .replace(/[_-]+/g, " ")
        .trim();

const normalizePaymentTerm = (value) =>
    String(value || "")
        .toLowerCase()
        .replace(/[_-]+/g, " ")
        .trim();

const isPaidStatus = (status) =>
    ["paid", "captured", "settled", "payment received", "fully paid"].includes(status);

const isPartialPaymentStatus = (status) =>
    ["partial", "partially paid", "partial paid", "partially settled"].includes(status);

const shipmentStats = computed(() => {
    const normalized = Array.isArray(props.shipments) ? props.shipments : [];
    const total = normalized.length;
    const delivered = normalized.filter((shipment) => /delivered|picked up/i.test(String(shipment.status || ""))).length;
    const inTransit = normalized.filter((shipment) => /transit|shipp/i.test(String(shipment.status || ""))).length;
    const pending = Math.max(total - delivered - inTransit, 0);
    const pickup = normalized.length > 0 && normalized.every((shipment) => /pickup|medusa/i.test(String(shipment.carrier || "")));

    return { total, delivered, inTransit, pending, pickup };
});

const lifecycle = computed(() => {
    const paymentStatus = normalizePaymentStatus(props.order?.payment_status || props.order?.paymentStatus);
    const paymentTerm = normalizePaymentTerm(props.order?.payment_term || props.order?.paymentTerm);
    const companyTermName = normalizePaymentTerm(props.order?.company?.payment_term?.name || "");
    const createdAt = formatDate(props.order?.created_at || props.order?.placedAt || "");
    const stats = shipmentStats.value;
    const usesNetTerms = /net\s*\d+/.test(companyTermName) || /net\s*\d+/.test(paymentTerm);
    const usesNet30 = usesNetTerms; // keep compat alias
    const usesCOD = /cash\s*on\s*delivery|\bcod\b/.test(paymentTerm);
    const termLabel = (() => {
        const match = (companyTermName || paymentTerm).match(/net\s*\d+/i);
        return match ? match[0].replace(/\s+/, " ").replace(/net (\d+)/i, "Net $1") : "Net Terms";
    })();
    const invoiceDueAt = props.order?.invoice?.[0]?.due_at || null;
    const isOverdue = usesNetTerms && !isPaidStatus(paymentStatus) && invoiceDueAt && new Date(invoiceDueAt) < new Date();
    const isPartialPayment =
        isPartialPaymentStatus(paymentStatus) ||
        /partial/.test(String(props.order?.payment_state || props.order?.paymentState || "").toLowerCase());
    const isPaidPayment = isPaidStatus(paymentStatus);

    const summaryLabel = stats.total
        ? stats.delivered === stats.total
            ? `${stats.total} package${stats.total === 1 ? "" : "s"} delivered`
            : `${stats.delivered} delivered · ${stats.inTransit} in transit`
        : "No shipments yet";

    const statusMeta = (() => {
        if (isOverdue || /hold|overdue|past[- ]?due/.test(paymentStatus)) {
            return { text: "Action needed", tone: "action" };
        }

        if (isPaidPayment || isPartialPayment) {
            return { text: stats.delivered === stats.total && stats.total > 0 ? "Complete" : "Arriving", tone: isPaidPayment && stats.delivered === stats.total && stats.total > 0 ? "complete" : "neutral" };
        }

        if (stats.delivered === stats.total && stats.total > 0) {
            return { text: "Complete", tone: "complete" };
        }

        return { text: stats.inTransit > 0 || stats.delivered > 0 ? "Shipped" : "Shipping soon", tone: "neutral" };
    })();

    const netTermsPaymentStep = usesNetTerms ? (() => {
        if (isPaidPayment) {
            return {
                key: "payment",
                state: "done",
                caption: "DONE",
                title: `Paid on ${termLabel}`,
                detail: invoiceDueAt ? `Paid by ${formatDate(invoiceDueAt)}` : "",
                icon: "payment",
            };
        }
        if (isOverdue) {
            return {
                key: "payment",
                state: "overdue",
                caption: "OVERDUE",
                title: "Payment overdue",
                detail: invoiceDueAt ? `Was due ${formatDate(invoiceDueAt)}` : "",
                icon: "overdue",
            };
        }
        if (isPartialPayment) {
            return {
                key: "payment",
                state: "partial",
                caption: "PARTIAL",
                title: `Partially paid · ${termLabel}`,
                detail: invoiceDueAt ? `Due ${formatDate(invoiceDueAt)}` : "",
                icon: "payment",
            };
        }
        return {
            key: "payment",
            state: "future",
            caption: "",
            title: `Pay on ${termLabel}`,
            detail: invoiceDueAt ? `Due ${formatDate(invoiceDueAt)}` : "",
            icon: "payment",
        };
    })() : null;

    const paymentStep = usesNetTerms ? null : (() => {
        if (isPartialPayment) {
            return {
                key: "payment",
                state: "partial",
                caption: "PARTIAL",
                title: "Payment partially received",
                detail: props.order?.balance_due || props.order?.balanceDue ? `Balance ${props.order?.balance_due || props.order?.balanceDue}` : "",
                icon: "payment",
            };
        }
        if (isPaidPayment) {
            return {
                key: "payment",
                state: "done",
                caption: "DONE",
                title: "Payment confirmed",
                detail: "",
                icon: "payment",
            };
        }
        if (/hold|overdue|past[- ]?due/.test(paymentStatus)) {
            return {
                key: "payment",
                state: "future",
                caption: "",
                title: "Clear overdue balance",
                detail: "",
                icon: "payment",
            };
        }
        return {
            key: "payment",
            state: "future",
            caption: "",
            title: "Send your payment",
            detail: "",
            icon: "payment",
        };
    })();

    const pickPackState = stats.total === 0 ? "future" : stats.pending > 0 ? "partial" : "done";
    const shipState = stats.total === 0 ? "future" : stats.inTransit > 0 || stats.delivered > 0 ? "done" : "future";
    const deliveredState = stats.total === 0 ? "future" : stats.delivered > 0 && stats.delivered < stats.total ? "partial" : stats.delivered === stats.total ? "done" : stats.pickup ? "done" : "future";

    const baseSteps = [
        {
            key: "received",
            state: "done",
            caption: "DONE",
            title: "Order received",
            detail: "",
            icon: "check",
        },
        {
            key: "pack",
            state: pickPackState,
            caption: pickPackState === "done" ? "DONE" : "",
            title: "Pick & pack",
            detail: "",
            icon: "pack",
        },
        {
            key: "ship",
            state: shipState,
            caption: shipState === "done" ? "DONE" : "",
            title: stats.pickup ? "Ready for pickup" : "Order shipped",
            detail: stats.pickup ? "" : stats.inTransit > 0 ? (stats.inTransit > 1 ? `${stats.inTransit} shipments in transit` : "In transit") : "",
            icon: "ship",
        },
        {
            key: "delivered",
            state: deliveredState,
            caption: deliveredState === "partial" ? "PARTIAL" : deliveredState === "done" ? "DONE" : "",
            title: stats.pickup ? "Picked up" : deliveredState === "partial" ? "Partially delivered" : "Delivered",
            detail: stats.total && stats.delivered > 0 && stats.delivered < stats.total ? `${stats.delivered} of ${stats.total} · more on the way` : stats.total === stats.delivered && stats.total > 0 ? formatDate(props.order?.delivered_at || props.order?.deliveredAt || "") : stats.total > 0 ? (props.order?.eta_from || props.order?.etaFrom ? `${formatDate(props.order?.eta_from || props.order?.etaFrom)} – ${formatDate(props.order?.eta_to || props.order?.etaTo)}` : "") : "",
            icon: "delivered",
        },
    ];

    const codPaymentStep = usesCOD
        ? {
            ...(paymentStep || {
                key: "payment",
                state: "future",
                caption: "",
                title: "Payment on delivery",
                detail: "",
                icon: "payment",
            }),
            key: "payment-cod",
            title: (paymentStep && paymentStep.state !== "future") ? paymentStep.title : "Payment on delivery",
        }
        : null;

    const steps = usesCOD
        ? [...baseSteps, codPaymentStep]
        : usesNetTerms
            ? [...baseSteps, netTermsPaymentStep]
            : [baseSteps[0], paymentStep, ...baseSteps.slice(1)];

    return {
        steps: steps.filter((step) => step && step.title),
        statusText: statusMeta.text,
        statusTone: statusMeta.tone,
        summary: summaryLabel,
        updatedLabel: "Updated 4 min ago",
    };
});
</script>

<style scoped>
.lc-card {
    background: #fff;
    border: 1px solid #e6e8ee;
    border-radius: 14px;
    overflow: hidden;
    padding: 16px 18px 18px;
}

.lc-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
}

.lc-title {
    font-family: Geist, Söhne, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 15px;
    line-height: 1.2;
    font-weight: 600;
    color: #0b1220;
}

.lc-status {
    font-weight: 400;
    color: #94a3b8;
    font-size: 15px;
}

.lc-status--action {
    color: #d14343;
}

.lc-status--complete {
    color: #15803d;
}

.lc-status--neutral {
    color: #94a3b8;
}

.lc-refresh {
    appearance: none;
    border: 1px solid transparent;
    background: transparent;
    font-family: inherit;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    border-radius: 6px;
    color: #94a3b8;
    font-size: 11px;
    cursor: pointer;
    transition: color 120ms, background 120ms, border-color 120ms;
}

.lc-refresh:hover {
    background: #f8fafc;
    color: #64748b;
    border-color: #e2e8f0;
}

.lc-refresh-icon {
    display: inline-flex;
}

.lc-refresh-icon svg {
    display: block;
}

.lc-progress {
    display: grid;
    align-items: start;
    column-gap: 0;
}

.lc-step {
    position: relative;
    min-width: 0;
}

.lc-step-circle {
    width: 32px;
    height: 32px;
    border-radius: 9999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 1;
    background: #fff;
    border: 1px solid #d7dce6;
    color: #cbd5e1;
}

.lc-step--done .lc-step-circle {
    background: #111c36;
    border-color: #111c36;
    color: #fff;
}

.lc-step--partial .lc-step-circle {
    background: linear-gradient(90deg, #111c36 50%, #fff 50%);
    border-color: #111c36;
    color: #fff;
}

.lc-step-circle--partial svg {
    transform: translateX(-1px);
    mix-blend-mode: difference;
}

.lc-step-icon {
    stroke-width: 2;
}

.lc-step--future .lc-step-circle {
    background: #fff;
    border-color: #d7dce6;
    color: #c9d1df;
}

.lc-step--overdue .lc-step-circle {
    background: #dc2626;
    border-color: #dc2626;
    color: #fff;
}

.lc-step--overdue .lc-step-caption {
    color: #dc2626;
}

.lc-step-line {
    position: absolute;
    top: 16px;
    left: 32px;
    right: -6px;
    height: 1px;
    z-index: 0;
    background: #d9dee8;
}

.lc-step-line--done {
    background: #111c36;
}

.lc-step-line--partial {
    background: none;
    border-top: 1px dashed #111c36;
    height: 0;
    top: 16px;
}

.lc-step-line--future {
    background: repeating-linear-gradient(90deg, #d9dee8 0, #d9dee8 4px, transparent 4px, transparent 8px);
}

.lc-step-caption {
    margin-top: 10px;
    min-height: 14px;
    padding-right: 16px;
    font-size: 10px;
    line-height: 1;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #16a34a;
}

.lc-step--future .lc-step-caption {
    color: transparent;
}

.lc-step--partial .lc-step-caption {
    color: #0f172a;
}

.lc-step--done .lc-step-caption {
    color: #16a34a;
}

.lc-step-title {
    margin-top: 6px;
    padding-right: 16px;
    font-size: 14px;
    line-height: 1.25;
    font-weight: 600;
    color: #0f172a;
}

.lc-step--future .lc-step-title {
    color: #94a3b8;
}

.lc-step--partial .lc-step-title {
    color: #0f172a;
}

.lc-step-desc {
    margin-top: 4px;
    padding-right: 16px;
    font-size: 11px;
    line-height: 1.4;
    color: #94a3b8;
}

.lc-step:last-child .lc-step-caption,
.lc-step:last-child .lc-step-title,
.lc-step:last-child .lc-step-desc {
    padding-right: 0;
}

@media (max-width: 900px) {
    .lc-card {
        padding: 14px 14px 16px;
    }

    .lc-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 6px;
        margin-bottom: 14px;
    }

    .lc-refresh {
        padding-left: 0;
    }

    .lc-progress {
        grid-template-columns: 1fr !important;
        gap: 14px;
    }

    .lc-step {
        min-width: 0;
        min-height: 40px;
        padding-left: 48px;
        padding-right: 0;
    }

    .lc-step-circle {
        position: absolute;
        left: 0;
        top: 0;
        width: 30px;
        height: 30px;
    }

    .lc-step-line {
        left: 15px !important;
        right: auto !important;
        top: 30px !important;
        bottom: -14px;
        width: 1px !important;
        height: auto !important;
        border: none !important;
        background: #d9dee8 !important;
    }

    .lc-step-line--done {
        background: #111c36 !important;
    }

    .lc-step-line--partial,
    .lc-step-line--future {
        background: #d9dee8 !important;
    }

    .lc-step-caption,
    .lc-step-title,
    .lc-step-desc {
        padding-right: 0;
    }

    .lc-step-caption {
        margin-top: 0;
        min-height: 10px;
    }

    .lc-step-title {
        margin-top: 2px;
    }

    .lc-step-desc {
        margin-top: 2px;
    }
}
</style>
